import moment from "moment";
import React from "react";

import {
  getAddress,
  getAfterTaxRefundAmount,
  getImmediateTaxRefundAmount,
  getMethodName,
  getOrderItemName,
  getOrderNumber,
  getOrderOrigin,
  getReceiverPhone,
  getRetailStateLabel,
  getTypeLabel,
} from "../reducers/order";
import Constant from "./Constants";
import _ from "lodash";
import { numberToCurrency, numberWithCommas, phoneNumberFormat } from "./Utils";
import {
  INVENTORY_TRANSACTION_BY_REASON,
  ORDER_ORIGIN,
  ORDER_SUB_ORIGIN,
  RETURN_REASON,
  SHIPPING_COMPANY_BY_CODE,
} from "./GuideData";
import Constants from "./Constants";
import { getCustomerName, getCustomerPhone } from "../reducers/customer";

const RETURN_REASON_LABEL_BY_KEY = RETURN_REASON.reduce((obj, r) => {
  obj[r.key] = r.label;
  return obj;
}, {});

const VISIBLE_ORDER_ITEM_TYPE = [
  "sku",
  "custom",
  "reward",
  "credit",
  "coupon",
  "discount",
];

export const getPaymentInfo = (order) => {
  const orderAmount = getOrderAmount(order);
  const useCredit = getCreditAmount(order);
  const totalDiscountAmount = getDiscountAmount(order);
  const pointDiscountAmount = getRewardDiscountAmount(order);
  const discountAmount = totalDiscountAmount - pointDiscountAmount;
  const immediateTaxRefundAmount = getImmediateTaxRefundAmount(order) || 0;
  const afterTaxRefundAmount = getAfterTaxRefundAmount(order) || 0;
  const shippingFee = getShippingFee(order);
  const deliveryFee = getDeliveryFee(order);
  const paidAmount = getPaidAmount(order, true);
  const refundAmount = getRefundAmount(order);
  const payAmount = getPayAmount(order);
  const savePoint = order.pointSaved || 0;
  const tax = order.tax || 0;

  let unPaidAmount = 0;
  // NOTE: 정책 상 UI에서 환불금액을 미결제금액으로 보여주지 않음
  if (order.state === "created") {
    unPaidAmount = payAmount - paidAmount;
  } else {
    unPaidAmount = payAmount - getPaidAmount(order, false) - shippingFee;
  }
  if (unPaidAmount < 0) {
    unPaidAmount = 0;
  }
  return {
    useCredit,
    discountAmount,
    totalDiscountAmount,
    pointDiscountAmount,
    immediateTaxRefundAmount,
    afterTaxRefundAmount,
    shippingFee,
    deliveryFee,
    orderAmount,
    paidAmount,
    unPaidAmount,
    refundAmount,
    savePoint,
    payAmount,
    tax,
  };
};

export const brushupCharges = (charges) => {
  let _charges = [];
  (charges || []).forEach((charge) => {
    if (isPaymentCharge(charge)) {
      _charges = [
        ..._charges,
        {
          method: getMethodName(charge),
          extraMethod:
            (charge.method === "extra" && charge.extraMethod) || null,
          approvalNumber: charge.approvalNumber,
          note: charge.note || "",
          amount: charge.amount,
          createdAt: moment(charge.createdAt).format(
            Constant.MOMENT.DATETIME_FORMAT,
          ),
        },
      ];
    }
  });

  return _charges;
};

export const getPaymentMethodsLabel = (order) => {
  let brushedCharges = brushupCharges(order.charges);
  return brushedCharges.length > 0
    ? brushedCharges.map((c) => c.method).join(", ")
    : "";
};

export const isPaymentCharge = (charge) => {
  return !["refund", "credit", "point"].includes(charge.method);
};

export const filterRefundCharge = (charge) => {
  return !["refund"].includes(charge.method);
};

export const getPaymentCharges = (charges) => {
  return (charges || []).filter((charge) => isPaymentCharge(charge));
};

export const getRefundCharges = (charges) => {
  return (charges || []).filter(
    (charge) => isPaymentCharge(charge) && !_.isEmpty(charge.refunds),
  );
};

export const brushupRefundItems = (charges) => {
  const refundCharges = getRefundCharges(charges);
  return refundCharges.map((item) => ({
    method: getMethodName(item, true),
    items: (item.refunds || []).map((refund) => ({
      right: `-${numberToCurrency(refund.amount)}`,
      left: moment(refund.createdAt).format(Constant.MOMENT.DATETIME_FORMAT),
      mid: RETURN_REASON_LABEL_BY_KEY[refund.reason],
    })),
  }));
};

export const getOrderAmount = (order) => {
  const amount = _.sumBy(filterOrderItems(order?.items), "amount") || 0;
  return amount;
};

export const filterOrderItems = (orderItems) => {
  return (orderItems || []).filter((item) =>
    ["sku", "credit", "custom"].includes(item.type),
  );
};

export const getShippingFee = (order) => {
  // NOTE: 카페24 배송비 환불 시, orderItem.amount는 그대로지만 order.fee가 0으로 변경된다.
  // 그래서 실제 배송비는 order.fee를 참조
  if (!getDeliveryFee(order)) {
    const shippingFee = order.fee;
    return shippingFee;
  } else {
    return 0;
  }
};

export const getDeliveryFee = (order) => {
  const shippingFee =
    _.sumBy(
      (order?.items || []).filter((item) => ["delivery"].includes(item.type)),
      "amount",
    ) || 0;

  return shippingFee;
};

export const getDiscountAmount = (order) => {
  const discountAmount =
    _.sumBy(
      (order?.items || []).filter((item) =>
        ["coupon", "discount"].includes(item.type),
      ),
      "amount",
    ) || 0;

  return discountAmount + getRewardDiscountAmount(order);
};

export const getRewardDiscountAmount = (order) => {
  const discountAmount =
    _.sumBy(
      (order?.items || []).filter((item) => ["reward"].includes(item.type)),
      "amount",
    ) || 0;

  return discountAmount + getCafe24PointAmount(order);
};

export const getCafe24PointAmount = (order) => {
  let cafe24DiscountAmount = 0;
  (order?.charges || []).forEach((charge) => {
    if (charge.method === "point") {
      cafe24DiscountAmount += charge.amount || 0;
      // (charge.refunds || []).forEach((refund) => {
      //   cafe24DiscountAmount -= refund.amount || 0;
      // });
    }
  });

  return cafe24DiscountAmount;
};

export const getCreditAmount = (order) => {
  let creditAmount = 0;
  (order?.charges || []).forEach((charge) => {
    if (charge.method === "credit") {
      creditAmount += charge.amount || 0;
      (charge.refunds || []).forEach((refund) => {
        creditAmount -= refund.amount || 0;
      });
    }
  });

  return creditAmount;
};

export const getPayAmount = (order) => {
  const orderAmount = getOrderAmount(order);
  const discountAmount = getDiscountAmount(order);
  const creditAmount = getCreditAmount(order);
  const immediateTaxRefundAmount = getImmediateTaxRefundAmount(order) || 0;
  let payAmount =
    orderAmount -
    discountAmount -
    creditAmount -
    immediateTaxRefundAmount +
    // NOTE: 총 주문금액에는 fee가 빠지지만 결제된 금액에는 fee가 포함되기때문에 fee를 더해준다.
    (order.fee || 0);
  return payAmount;
};

// NOTE: for UI
export const getHeadAmount = (order) => {
  const payAmount = getPayAmount(order);
  if (order.state === "created") {
    return payAmount;
  } else {
    const paidAmount = getPaidAmount(order, false);
    return paidAmount > payAmount ? payAmount : paidAmount;
  }
};

export const getRefundAmount = (order) => {
  let refundAmount = 0;
  const now = new Date();
  (order.charges || []).forEach((charge) => {
    if (isPaymentCharge(charge)) {
      if (!_.isEmpty(charge.refunds)) {
        charge.refunds.forEach((refund) => {
          // const isReturnedAfterPaid =
          //   new Date(refund.createdAt).getTime() >
          //   (new Date(order.paidAt) || now).getTime();
          // if (isReturnedAfterPaid) {
          refundAmount += _.sumBy(charge.refunds, "amount");
          // }
        });
      }
    }
  });
  return refundAmount;
};

export const getPaidAmount = (order, includeRefund = true) => {
  if (order.paidAmount) {
    return order.paidAmount;
  } else {
    let paidAmount = 0;
    (order.charges || []).forEach((charge) => {
      if (isPaymentCharge(charge)) {
        paidAmount = paidAmount + charge.amount;
        if (!_.isEmpty(charge.refunds) && includeRefund) {
          paidAmount -= _.sumBy(charge.refunds, "amount");
        }
      }
    });

    return paidAmount;
  }
};

export const brushupReturns = (order, key) => {
  const returns = {};
  for (let ret of order.returns || []) {
    for (let item of ret.items || []) {
      if (item[key] in returns) {
        returns[item[key]] = {
          ...returns[item[key]],
          quantity: returns[item[key]].quantity + item.quantity,
          amount: returns[item[key]].amount + item.amount,
        };
      } else {
        returns[item[key]] = item;
      }
    }
  }

  return returns;
};

const brushupRewardOrderItem = (item) => {
  return {
    leftTitle: `포인트사용(${item.name}, ${item.point}P)`,
    midTitle: item.quantity ? `x${item.quantity}` : "",
    rightTitle:
      (item.discountMethod ? "- " : "") + numberToCurrency(item.amount),
    note: item.note,
    itemId: item.parentId,
  };
};

const brushupOrderItemModifiers = (modifiers) => {
  return (modifiers || []).map((mod) => ({
    left: mod.name,
    mid: mod.quantity ? `x${mod.quantity}` : "",
    right: numberToCurrency(mod.amount),
  }));
};

const brushupOrderItemDiscounts = (discounts) => {
  let _discounts = [];
  (discounts || []).forEach((dis) => {
    if (dis.amount > 0 && dis.scope === "partial" && dis.type !== "reward") {
      _discounts = [
        ..._discounts,
        {
          left: dis.name,
          mid: dis.quantity > 0 ? `x${dis.quantity}` : "",
          right: `${"- "}${numberToCurrency(dis.amount)}`,
        },
      ];
    }
  });
  return _discounts;
};

const getQuantityLabel = (orderItem) => {
  const isVoided = orderItem.quantity === 0 && orderItem.voidQuantity > 0;
  let _quantityLabel = "";
  if (isVoided) {
    _quantityLabel = `x${orderItem.voidQuantity}`;
  } else if (orderItem.quantity) {
    if (!(orderItem.quantity === 1 && orderItem.type === "coupon")) {
      _quantityLabel = `x${orderItem.quantity}`;
    }
  }
  return _quantityLabel;
};

const getRetailQuantityLabel = (orderItem) => {
  let _quantityLabel = "";
  if (orderItem.quantity) {
    if (!(orderItem.quantity === 1 && orderItem.type === "coupon")) {
      _quantityLabel = `x${orderItem.receivedQuantity || orderItem.quantity}`;
    }
  }
  return _quantityLabel;
};

const getExchangeOrder = (order, orderItemId) => {
  for (let exchange of order.exchanges || []) {
    if ((exchange.items || []).map((item) => item.id).includes(orderItemId)) {
      return exchange;
    }
  }
  return {};
};

export const getExchangeItemIds = (order) => {
  let itemIds = [];
  (order.exchanges || []).forEach((e) => {
    if ((e.items || []).length > 0) {
      itemIds = [...itemIds, ...(e.items || []).map((i) => i.id)];
    }
  });
  return itemIds;
};

export const brushupRetailOption = (order, item) => {
  let name = null;
  if (item.retailOption === "instant" && order.retailOption === "shipping") {
    name = "현장수령(즉시출고)";
  }
  return name;
};

export const brushupOrderItems = (order) => {
  const items = order.items;
  let orderItems = [];

  for (let item of (items || []).filter((orderItem) =>
    VISIBLE_ORDER_ITEM_TYPE.includes(orderItem.type),
  )) {
    if (item.type === "reward") {
      orderItems = [...orderItems, brushupRewardOrderItem(item)];
    } else {
      const modifiers = brushupOrderItemModifiers(item.modifiers);
      const discounts = brushupOrderItemDiscounts(item.discounts);
      const diningOption = brushUpDiningOption(item, order);
      const retailOption = [{ left: brushupRetailOption(order, item) }];
      const isPartialDiscountOrCoupon =
        (item.discountIncludeIds || []).length > 0 ||
        (item.discountExcludeIds || []).length > 0;
      const exchangeOrder = getExchangeOrder(order, item.id);
      const hasExchange =
        item.referType === "exchange" || !_.isEmpty(exchangeOrder);
      // NOTE: 부분할인은 subItems에 포함됨
      if (!isPartialDiscountOrCoupon) {
        const isVoided = item.quantity === 0 && item.voidQuantity > 0;
        orderItems = [
          ...orderItems,
          {
            leftTitle: item.name,
            midTitle: getQuantityLabel(item),
            rightTitle:
              (item.discountMethod ? "- " : "") + numberToCurrency(item.amount),
            note: item.note,
            subItems: item.description
              ? [
                  { left: item.description },
                  ...modifiers,
                  ...discounts,
                  ...diningOption,
                  ...retailOption,
                ]
              : [...modifiers, ...discounts, ...diningOption, ...retailOption],
            itemId: item.parentId,
            createdAt: item.createdAt
              ? moment(item.createdAt).format(Constant.MOMENT.DATETIME_FORMAT)
              : null,
            isVoided: isVoided,
            isExchange:
              hasExchange &&
              item.type !== "coupon" &&
              item.type !== "discount" &&
              item.type !== "reward",
            exchangedAt: hasExchange
              ? moment(
                  !_.isEmpty(exchangeOrder)
                    ? exchangeOrder.createdAt
                    : item.createdAt,
                ).format(Constant.MOMENT.DATETIME_FORMAT)
              : null,
            originalQuantity: item.quantity,
            id: item.id,
          },
        ];
      }
    }
  }
  return orderItems;
};

const getBrushedReturnsForExchangeReturn = (order) => {
  let _returns = _.cloneDeep(
    (order.returns || []).filter(
      (ret) =>
        ret.exchangeGroupId ||
        (
          (ret.items || []).filter((item) => item.referType === "exchange") ||
          []
        ).length > 0,
    ),
  );
  for (let ret of _returns || []) {
    ret.items = ret.items
      .map((r) => ({
        ...r,
        exchangeGroupId: ret.exchangeGroupId,
      }))
      .filter(
        (i) =>
          VISIBLE_ORDER_ITEM_TYPE.includes(i.type) &&
          (i.referType === "exchange" || i.exchangeGroupId) &&
          !(i.type === "coupon" && i.referId === "auto"),
      );
  }
  for (let x = 0; x < (_returns || []).length; x++) {
    const exchangesByGroupId = (order.exchanges || []).reduce((obj, e) => {
      obj[e.exchangeGroupId] = e;
      return obj;
    }, {});
    const orderExchange = exchangesByGroupId[_returns[x].exchangeGroupId] || {};
    _returns[x] = {
      ..._returns[x],
      createdAt:
        orderExchange.createdAt || (_returns[x]?.items || [])[0]?.createdAt,
    };
  }
  _returns = _returns.filter((ret) => (ret.items || []).length > 0);
  return _returns;
};

export const brushupExchangeItems = (order) => {
  let _returns = getBrushedReturnsForExchangeReturn(order);
  for (let x = 0; x < (_returns || []).length; x++) {
    for (let y = 0; y < (_returns[x].items || []).length; y++) {
      const item = _returns[x].items[y];
      const modifiers = brushupOrderItemModifiers(item.modifiers);
      const discounts = brushupOrderItemDiscounts(item.discounts);
      const inventoryText = item.inventoryReason
        ? {
            left: `반품처리: ${
              INVENTORY_TRANSACTION_BY_REASON[item.inventoryReason].label
            }`,
          }
        : { left: null };
      _returns[x].items[y] = {
        leftTitle: item.name,
        midTitle: getQuantityLabel(item),
        rightTitle:
          (item.discountMethod ? "- " : "") + numberToCurrency(item.amount),
        note: item.note,
        subItems: item.description
          ? [
              { left: item.description },
              ...modifiers,
              ...discounts,
              inventoryText,
            ]
          : [...modifiers, ...discounts, inventoryText],
        itemId: item.parentId,
        createdAt: item.createdAt
          ? moment(item.createdAt).format(Constant.MOMENT.DATETIME_FORMAT)
          : null,
        referType: item.referType,
        id: item.id,
      };
    }
  }
  return _returns;
};

export const brushupReturnsWithoutExchange = (order, key) => {
  let _returns = _.cloneDeep(order.returns || []).filter(
    (r) => !r.exchangeGroupId,
  );
  // 최신 order schema 적용 후 교환반품 제외는 exchangeGroupId 로 판별
  for (let ret of _returns || []) {
    ret.items = (ret.items || []).filter(
      (item) =>
        item.referType !== "exchange" &&
        VISIBLE_ORDER_ITEM_TYPE.includes(item.type) &&
        (item.discountIncludeIds || []).length === 0 &&
        (item.discountExcludeIds || []).length === 0,
    );
  }
  for (let x = 0; x < (_returns || []).length; x++) {
    _returns[x] = {
      ...(_returns[x] || {}),
      createdAt:
        _returns[x]?.createdAt || (_returns[x]?.items || [])[0]?.createdAt,
    };
    for (let y = 0; y < (_returns[x].items || []).length; y++) {
      const item = _returns[x].items[y];
      if (item.type === "reward") {
        _returns[x].items[y] = brushupRewardOrderItem(item);
      } else {
        const modifiers = brushupOrderItemModifiers(item.modifiers);
        const discounts = brushupOrderItemDiscounts(item.discounts);
        const diningOption = brushUpDiningOption(item, order);
        const isVoided = item.quantity === 0 && item.voidQuantity > 0;
        _returns[x].items[y] = {
          leftTitle: item.name,
          midTitle: getRetailQuantityLabel(item),
          rightTitle:
            (item.discountMethod ? "- " : "") + numberToCurrency(item.amount),
          note: item.note,
          subItems: item.description
            ? [
                { left: item.description },
                ...modifiers,
                ...discounts,
                ...diningOption,
              ]
            : [...modifiers, ...discounts, ...diningOption],
          itemId: item.parentId,
          isVoided: isVoided,
          referType: item.referType,
          originalQuantity: item.quantity,
          id: item.id,
        };
      }
    }
  }
  return _returns.filter((ret) => (ret?.items || []).length > 0);
};

const brushUpDiningOption = (item, order) => {
  // NOTE: 상위 옵션이 "포장"일 때, 개별 아이템에서 "배달"을 눌렀다가 "포장"으로 수정한 경우
  // item.diningOption과 상위 diningOption이 동일하기때문에 문구 표기가 필요없다
  let name = null;
  if (item.diningOption) {
    if (item.diningOption === "forhere" && order.diningOption !== "forhere") {
      name = "매장주문";
    } else if (
      item.diningOption === "takeout" &&
      order.diningOption !== "takeout"
    ) {
      name = "포장";
    } else if (
      item.diningOption === "delivery" &&
      order.diningOption !== "delivery"
    ) {
      name = "배달";
    }
  }
  if (name) {
    return [
      {
        left: name,
        mid: null,
        right: null,
      },
    ];
  }
  return [];
};

export const brushupOrderTimelines = (order) => {
  let lines = [];
  let _origin = ORDER_ORIGIN.reduce((obj, o) => {
    obj[o.key] = o.label;
    return obj;
  }, {});
  let _subOrigin = ORDER_SUB_ORIGIN.reduce((obj, o) => {
    obj[o.key] = o.label;
    return obj;
  }, {});
  lines = [
    ...lines,
    {
      key: "created",
      label: order.subOrigin
        ? order.subOrigin in _subOrigin
          ? _subOrigin[order.subOrigin.toLowerCase()]
          : order.subOrigin
        : _origin[(order.origin || "pos")?.toLowerCase()],
      createdAt: order.createdAt,
    },
  ];

  for (let charge of (order.charges || []).filter(
    (c) => c.method !== "refund",
  )) {
    lines = [
      ...lines,
      {
        key: "paid",
        label: `${getMethodName(charge) || ""}: ${numberWithCommas(
          charge.amount,
        )}`,
        createdAt: charge.createdAt || order.paidAt,
      },
    ];
    if ((charge.refunds || []).length > 0) {
      for (let refund of charge.refunds) {
        lines = [
          ...lines,
          {
            key: "refunded",
            label: `${getMethodName(charge)}: ${numberWithCommas(
              refund.amount,
            )}`,
            createdAt: refund.createdAt || order.returnedAt,
          },
        ];
      }
    }
  }

  if ((order.exchanges || []).length > 0) {
    for (let exchange of order.exchanges) {
      lines = [
        ...lines,
        {
          key: "exchangeOut",
          label: `교환품: ${(exchange.items || [])
            .filter((it) => it.type === "sku" || it.type === "custom")
            .map((item) => item.name)
            .join(", ")}`,
          createdAt: exchange.createdAt,
        },
      ];
    }
  }
  if (
    ((order.returns || []).filter((ret) => !ret.exchangeGroupId) || []).length >
    0
  ) {
    for (let ret of order.returns.filter((r) => !r.exchangeGroupId)) {
      lines = [
        ...lines,
        {
          key: "returned",
          label: `반품: ${(ret.items || [])
            .filter((it) => it.type === "sku" || it.type === "custom")
            .map((item) => item.name)
            .join(", ")}`,
          createdAt: ret.createdAt || (ret.items || [])[0]?.createdAt,
        },
      ];
    }
  }
  if (
    ((order.returns || []).filter((ret) => ret.exchangeGroupId) || []).length >
    0
  ) {
    for (let ret of order.returns.filter(
      (r) => r.exchangeGroupId && (r.items || []).length > 0,
    )) {
      lines = [
        ...lines,
        {
          key: "exchangeIn",
          label: `교환반품: ${(ret.items || [])
            .filter((it) => it.type === "sku" || it.type === "custom")
            .map((item) => item.name)
            .join(", ")}`,
          createdAt: ret.createdAt || order.returnedAt,
        },
      ];
    }
  }
  if (order.retailOption === "shipping") {
    if (order.confirmedAt) {
      lines = [
        ...lines,
        {
          key: "confirmed",
          createdAt: order.confirmedAt,
        },
      ];
    }
    if (order.shippedAt) {
      let receiver = (order.receivers || [])[0];
      let info = !_.isEmpty(receiver)
        ? `송장번호: ${receiver.trackingNumber || ""}, 택배사: ${
            SHIPPING_COMPANY_BY_CODE[receiver?.shippingCompanyCode] || ""
          }`
        : null;
      lines = [
        ...lines,
        {
          key: "shipped",
          createdAt: order.shippedAt,
          label: info,
        },
      ];
    }
  }
  return _.sortBy(lines, "createdAt");
};

const getCustomerLabel = (order, isOutsourced, isExport) => {
  switch (order.origin) {
    case "cafe24":
    case "imweb":
      return order.name || order.phone ? (
        isExport ? (
          `${order.name}\r\n${phoneNumberFormat(order.phone)}`
        ) : (
          <>
            <div>{order.name}</div>
            <div>{phoneNumberFormat(order.phone)}</div>
          </>
        )
      ) : (
        ""
      );

    default:
      let customer = order.customer || order.orderCustomer;
      return customer && (customer.name || customer.phone) ? (
        isExport ? (
          `${getCustomerName(
            customer.name,
            isOutsourced,
            customer.status === "anonymous",
            false,
            customer.agreements,
          )}\r\n ${
            customer.status === "anonymous"
              ? ""
              : getCustomerPhone(
                  customer.phone,
                  isOutsourced,
                  false,
                  customer.agreements,
                )
          }`
        ) : (
          <>
            <div>
              {getCustomerName(
                customer.name,
                isOutsourced,
                customer.status === "anonymous",
                false,
                customer.agreements,
              )}
            </div>
            <div>
              {customer.status === "anonymous"
                ? ""
                : getCustomerPhone(
                    customer.phone,
                    isOutsourced,
                    false,
                    customer.agreements,
                  )}
            </div>
          </>
        )
      ) : (
        ""
      );
  }
};

const getEmployeeName = (employeesById, _employeeId) => {
  return employeesById[_employeeId]
    ? (employeesById[_employeeId]?.lastName || "") +
        (employeesById[_employeeId]?.firstName ||
          employeesById[_employeeId]?.nickname ||
          "")
    : "";
};

export const brushupOrderForList = (
  order,
  isOutsourced,
  employeesById,
  orderNumberWithDate,
  locationsById,
  isExport,
) => {
  let _order = {};
  const payment = getPaymentInfo(order);
  // 총 주문수량 계산할때
  let countableItems = (order?.items || []).filter(
    (item) =>
      item.type === "sku" || item.type === "credit" || item.type === "custom",
  );
  // 주문제품 리스트 보여줄때
  let skuOrderItems = (order?.items || []).filter(
    (item) =>
      item.type === "sku" || item.type === "credit" || item.type === "custom",
  );
  const receiver = (order.receivers || [])[0];
  _order = {
    id: order.id,
    status:
      order.state === "created"
        ? "미결제"
        : order.state === "paid"
          ? "결제완료"
          : "환불",
    shippingStatus: getRetailStateLabel(order.retailState),
    confirmedEmployee: order.confirmedEmployeeId
      ? getEmployeeName(employeesById, order.confirmedEmployeeId)
      : "",
    type: getTypeLabel(order),
    location: locationsById[order.orderLocationId]?.name || "",
    locationCode: locationsById[order.orderLocationId]?.code || "",
    shippingLocation: order.shippingLocationId
      ? locationsById[order.shippingLocationId]?.name || ""
      : "",
    orderedAt: moment(order.createdAt).format(Constants.MOMENT.DATETIME_FORMAT),
    paidAt: order.paidAt
      ? moment(order.paidAt).format(Constants.MOMENT.DATETIME_FORMAT)
      : "",
    returnedAt: order.returnedAt
      ? moment(order.returnedAt).format(Constants.MOMENT.DATETIME_FORMAT)
      : "",
    orderNumber: `${
      orderNumberWithDate
        ? `${moment(order.createdAt).format("YYYYMMDD")}-`
        : ""
    }${getOrderNumber(
      order.id,
      order.number,
      order.numberPrefix,
      order.originId,
    )}`,
    orderNumberWithDate: `${moment(order.createdAt).format(
      "YYYYMMDD",
    )}-${getOrderNumber(
      order.id,
      order.number,
      order.numberPrefix,
      order.originId,
    )}`,
    origin: getOrderOrigin(order.origin),
    items: order.items.filter(
      (item) => item.type !== "discount" && item.type !== "reward",
    ),
    itemsName: (skuOrderItems || [])
      .map((item) => getOrderItemName(item))
      .join(", "),
    isCreditChage:
      (order.items || []).filter((item) => item.type === "credit").length > 0,
    customer: getCustomerLabel(order, isOutsourced, isExport),
    statusOrigin: order.state,
    quantity: order.quantity || _.sumBy(countableItems, "quantity"),
    orderAmount: payment.orderAmount,
    discountAmount: payment.discountAmount,
    pointDiscountAmount: payment.pointDiscountAmount,
    creditAmount: payment.useCredit,
    taxRefundAmount: payment.immediateTaxRefundAmount,
    afterTaxRefundAmount: payment.afterTaxRefundAmount || 0,
    shippingFee: getShippingFee(order),
    tax: order.tax || 0,
    refundAmount: payment.refundAmount || 0,
    unpaidAmount: payment.unPaidAmount,
    paidAmount: payment.paidAmount,
    receiverName: receiver?.name || "",
    receiverPhone: getReceiverPhone(receiver, isExport),
    receiverAddress: getAddress(order),
    shippingNote: receiver?.note || "",
    shippingCompany: receiver?.shippingCompanyCode
      ? SHIPPING_COMPANY_BY_CODE[Number(receiver.shippingCompanyCode)]
      : "",
    trackingNumber: receiver?.trackingNumber || "",
    privacyAgreedAt: receiver?.privacyAgreedAt
      ? moment(receiver.privacyAgreedAt).format(
          Constants.MOMENT.DATETIME_FORMAT,
        )
      : "",
    paymentMethod: getPaymentMethodsLabel(order),
    employee: order.createdEmployeeId
      ? getEmployeeName(employeesById, order.createdEmployeeId)
      : "",
    orderNote: order.note,
  };
  return _order;
};

export const brushupOrderItemForList = (
  order,
  locationsById,
  productsById,
  skusById,
  orderNumberWithDate,
  isExport,
  isOutsourced,
  employeesById,
) => {
  let items = [];
  let skuOrderItems = (order?.items || []).filter(
    (item) =>
      (!item.retailOption || item.retailOption !== "instant") &&
      (item.type === "sku" || item.type === "credit" || item.type === "custom"),
  );
  const receiver = (order.receivers || [])[0];
  for (
    let itemIndex = 0;
    itemIndex < (skuOrderItems || []).length;
    itemIndex++
  ) {
    const itemDiscounts = skuOrderItems[itemIndex].discounts || [];
    const pointDiscountAmount = _.sumBy(
      itemDiscounts.filter((d) => d.type === "reward"),
      "amount",
    );
    const nonPointDiscountAmount = _.sumBy(
      itemDiscounts.filter((d) => d.type !== "reward"),
      "amount",
    );
    const appliedDiscountAmount = pointDiscountAmount + nonPointDiscountAmount;
    const product = productsById[skuOrderItems[itemIndex].productId];
    const sku = skusById[skuOrderItems[itemIndex].parentId];
    const item = skuOrderItems[itemIndex];
    items = [
      ...items,
      {
        id: order.id,
        location: locationsById[order.orderLocationId]?.name || "",
        locationCode: locationsById[order.orderLocationId]?.code || "",
        origin: getOrderOrigin(order.origin),
        orderedAt: moment(item.createdAt || order.createdAt).format(
          Constants.MOMENT.DATETIME_FORMAT,
        ),
        paidAt: moment(item.createdAt || order.paidAt).format(
          Constants.MOMENT.DATETIME_FORMAT,
        ),
        orderNumber: `${
          orderNumberWithDate
            ? `${moment(order.createdAt).format("YYYYMMDD")}-`
            : ""
        }${getOrderNumber(
          order.id,
          order.number,
          order.numberPrefix,
          order.originId,
        )}`,
        orderNumberWithDate: `${moment(order.createdAt).format(
          "YYYYMMDD",
        )}-${getOrderNumber(
          order.id,
          order.number,
          order.numberPrefix,
          order.originId,
        )}`,
        itemOrderNumber: `${
          orderNumberWithDate
            ? `${moment(order.createdAt).format("YYYYMMDD")}-`
            : ""
        }${getOrderNumber(
          order.id,
          order.number,
          order.numberPrefix,
          order.originId,
        )}-${itemIndex + 1}`,
        type: item.retailOption ? getTypeLabel(item) : getTypeLabel(order),
        status: order.state === "created" ? "미결제" : "결제완료",
        statusOrigin: order.state === "created" ? "created" : "paid",
        shippingStatus: getRetailStateLabel(order.retailState),
        prodName: product ? product.name : skuOrderItems[itemIndex].name || "",
        skuName: sku ? sku.name : skuOrderItems[itemIndex].description || "",
        model: sku ? sku.model : "",
        barcode: sku ? sku.unit || sku.barcode : "",
        quantity: skuOrderItems[itemIndex].quantity,
        orderAmount: skuOrderItems[itemIndex].amount || 0,
        discountAmount: nonPointDiscountAmount,
        pointDiscountAmount,
        paidAmount:
          order.state === "created"
            ? 0
            : skuOrderItems[itemIndex].amount - appliedDiscountAmount,
        itemNote: skuOrderItems[itemIndex].note,
        customer: getCustomerLabel(order, isOutsourced, isExport),
        receiverName: receiver?.name || "",
        receiverPhone: getReceiverPhone(receiver, isExport),
        receiverAddress: getAddress(order),
        shippingNote: receiver?.note || "",
        shippingCompany: receiver?.shippingCompanyCode
          ? SHIPPING_COMPANY_BY_CODE[Number(receiver.shippingCompanyCode)]
          : "",
        trackingNumber: receiver?.trackingNumber || "",
        privacyAgreedAt: receiver?.privacyAgreedAt
          ? moment(receiver.privacyAgreedAt).format(
              Constants.MOMENT.DATETIME_FORMAT,
            )
          : "",
        employee: order.createdEmployeeId
          ? getEmployeeName(employeesById, order.createdEmployeeId)
          : "",
        orderNote: order.note || "",
      },
    ];
  }
  return items;
};

export const brushupOrderExchangeItemsForList = (
  order,
  locationsById,
  orderNumberWithDate,
  productsById,
  skusById,
  isExport,
  employeesById,
  isOutsourced,
) => {
  let items = [];
  let skuOrderItems = (order?.items || []).filter(
    (item) =>
      item.type === "sku" || item.type === "credit" || item.type === "custom",
  );
  let _returns = getBrushedReturnsForExchangeReturn(order);
  _returns = _returns.map((ret) => ({
    ...ret,
    items: (ret.items || []).filter((i) =>
      ["sku", "custom", "credit"].includes(i.type),
    ),
  }));
  const receiver = (order.receivers || [])[0];
  for (let ret of _returns || []) {
    for (
      let exchangeIndex = 0;
      exchangeIndex < (ret.items || []).length;
      exchangeIndex++
    ) {
      const product = productsById[ret.items[exchangeIndex].productId];
      const sku = skusById[ret.items[exchangeIndex].parentId];
      const exchangeItem = ret.items[exchangeIndex];
      const exchangeItemDiscounts = exchangeItem.discounts || [];
      const exchangePointDiscount = _.sumBy(
        exchangeItemDiscounts.filter((d) => d.type === "reward"),
        "amount",
      );
      const exchangeNonPointDiscount = _.sumBy(
        exchangeItemDiscounts.filter((d) => d.type !== "reward"),
        "amount",
      );
      const exchangeDiscountTotal =
        exchangePointDiscount + exchangeNonPointDiscount;
      items = [
        ...items,
        {
          id: order.id,
          location: locationsById[order.orderLocationId]?.name || "",
          locationCode: locationsById[order.orderLocationId]?.code||'',
          origin: getOrderOrigin(order.origin),
          orderedAt: moment(exchangeItem.createdAt || order.createdAt).format(
            Constants.MOMENT.DATETIME_FORMAT,
          ),
          paidAt: moment(exchangeItem.createdAt || order.paidAt).format(
            Constants.MOMENT.DATETIME_FORMAT,
          ),
          returnedAt: moment(ret.createdAt || order.returnedAt).format(
            Constants.MOMENT.DATETIME_FORMAT,
          ),
          orderNumber: `${
            orderNumberWithDate
              ? `${moment(order.createdAt).format("YYYYMMDD")}-`
              : ""
          }${getOrderNumber(
            order.id,
            order.number,
            order.numberPrefix,
            order.originId,
          )}`,
          itemOrderNumber: `${
            orderNumberWithDate
              ? `${moment(order.createdAt).format("YYYYMMDD")}-`
              : ""
          }${getOrderNumber(
            order.id,
            order.number,
            order.numberPrefix,
            order.originId,
          )}-${(skuOrderItems || []).length + exchangeIndex + 1}`,
          type: getTypeLabel(order),
          status: "교환반품",
          shippingStatus: getRetailStateLabel(order.retailState),
          statusOrigin: "exchanged",
          prodName: product ? product.name : exchangeItem.name || "",
          skuName: sku ? sku.name : exchangeItem.description || "",
          model: sku ? sku.model : "",
          barcode: sku ? sku.unit || sku.barcode : "",
          quantity: exchangeItem.quantity,
          orderAmount: exchangeItem.amount,
          discountAmount: exchangeNonPointDiscount,
          pointDiscountAmount: exchangePointDiscount,
          refundAmount: exchangeItem.amount - exchangeDiscountTotal,
          paidAmount:
            order.state === "created"
              ? 0
              : exchangeItem.amount - exchangeDiscountTotal,
          itemNote: exchangeItem.note,
          customer: getCustomerLabel(order, isOutsourced, isExport),
          receiverName: receiver?.name || "",
          receiverPhone: getReceiverPhone(receiver, isExport),
          shippingNote: receiver?.note || "",
          receiverAddress: getAddress(order),
          shippingCompany: receiver?.shippingCompanyCode
            ? SHIPPING_COMPANY_BY_CODE[Number(receiver.shippingCompanyCode)]
            : "",
          trackingNumber: receiver?.trackingNumber || "",
          privacyAgreedAt: receiver?.privacyAgreedAt
            ? moment(receiver.privacyAgreedAt).format(
                Constants.MOMENT.DATETIME_FORMAT,
              )
            : "",
          employee: order.createdEmployeeId
            ? getEmployeeName(employeesById, order.createdEmployeeId)
            : "",
          orderNote: order.note || "",
        },
      ];
    }
  }
  return items;
};

export const brushupOrderReturnItemsForList = (
  order,
  locationsById,
  orderNumberWithDate,
  productsById,
  skusById,
  isExport,
  employeesById,
  isOutsourced,
) => {
  let items = [];
  let skuOrderItems = (order?.items || []).filter(
    (item) =>
      item.type === "sku" || item.type === "credit" || item.type === "custom",
  );
  let exchangeReturns = getBrushedReturnsForExchangeReturn(order);
  let exchangeItems = [];
  exchangeReturns.forEach((er) => {
    if ((er.items || []).length > 0) {
      exchangeItems = [
        ...exchangeItems,
        ...er.items.filter((i) =>
          ["sku", "custom", "credit"].includes(i.type),
        ),
      ];
    }
  });
  let _returns = _.cloneDeep(order.returns || []).filter(
    (r) => !r.exchangeGroupId,
  );
  const receiver = (order.receivers || [])[0];
  for (let x = 0; x < (_returns || []).length; x++) {
    _returns[x].items = (_returns[x]?.items || []).filter(
      (item) =>
        item.referType !== "exchange" &&
        ["sku", "custom", "credit"].includes(item.type) &&
        (item.discountIncludeIds || []).length === 0 &&
        (item.discountExcludeIds || []).length === 0,
    );
    _returns[x] = {
      ...(_returns[x] || {}),
      createdAt:
        _returns[x]?.createdAt || (_returns[x]?.items || [])[0]?.createdAt,
    };
    for (let y = 0; y < (_returns[x].items || []).length; y++) {
      const item = _returns[x].items[y];
      const product = productsById[item.productId];
      const sku = skusById[item.parentId];
      const returnItemDiscounts = item.discounts || [];
      const returnPointDiscount = _.sumBy(
        returnItemDiscounts.filter((d) => d.type === "reward"),
        "amount",
      );
      const returnNonPointDiscount = _.sumBy(
        returnItemDiscounts.filter((d) => d.type !== "reward"),
        "amount",
      );
      const returnDiscountTotal = returnPointDiscount + returnNonPointDiscount;
      items = [
        ...items,
        {
          id: order.id,
          location: locationsById[order.orderLocationId]?.name || "",
          locationCode: locationsById[order.orderLocationId]?.code||'',
          origin: getOrderOrigin(order.origin),
          orderedAt: moment(item.createdAt || order.createdAt).format(
            Constants.MOMENT.DATETIME_FORMAT,
          ),
          paidAt: moment(item.createdAt || order.paidAt).format(
            Constants.MOMENT.DATETIME_FORMAT,
          ),
          returnedAt: moment(_returns[x].createdAt || order.returnedAt).format(
            Constants.MOMENT.DATETIME_FORMAT,
          ),
          orderNumber: `${
            orderNumberWithDate
              ? `${moment(order.createdAt).format("YYYYMMDD")}-`
              : ""
          }${getOrderNumber(
            order.id,
            order.number,
            order.numberPrefix,
            order.originId,
          )}`,
          itemOrderNumber: `${
            orderNumberWithDate
              ? `${moment(order.createdAt).format("YYYYMMDD")}-`
              : ""
          }${getOrderNumber(
            order.id,
            order.number,
            order.numberPrefix,
            order.originId,
          )}-${
            (skuOrderItems || []).length + (exchangeItems || []).length + y + 1
          }`,
          type: getTypeLabel(order),
          status: "반품",
          shippingStatus: getRetailStateLabel(order.retailState),
          statusOrigin: "returned",
          prodName: product ? product.name : item.name || "",
          skuName: sku ? sku.name : item.description || "",
          model: sku ? sku.model : "",
          barcode: sku ? sku.unit || sku.barcode : "",
          quantity: item.quantity,
          orderAmount: item.amount,
          discountAmount: returnNonPointDiscount,
          pointDiscountAmount: returnPointDiscount,
          refundAmount: item.amount - returnDiscountTotal,
          paidAmount: item.amount - returnDiscountTotal,
          itemNote: item.note,
          customer: getCustomerLabel(order, isOutsourced, isExport),
          receiverName: receiver?.name || "",
          receiverPhone: getReceiverPhone(receiver, isExport),
          shippingNote: receiver?.note || "",
          receiverAddress: getAddress(order),
          shippingCompany: receiver?.shippingCompanyCode
            ? SHIPPING_COMPANY_BY_CODE[Number(receiver.shippingCompanyCode)]
            : "",
          trackingNumber: receiver?.trackingNumber || "",
          privacyAgreedAt: receiver?.privacyAgreedAt
            ? moment(receiver.privacyAgreedAt).format(
                Constants.MOMENT.DATETIME_FORMAT,
              )
            : "",
          employee: order.createdEmployeeId
            ? getEmployeeName(employeesById, order.createdEmployeeId)
            : "",
          orderNote: order.note || "",
        },
      ];
    }
  }
  return items;
};
